import {
  useCreateNylasCalendarEventMutation,
  useUpdateNylasCalendarEventMutation,
} from '../../../../../generated/graphql';
import { customToast } from '../../../../components/ToastAlert/customToast';
import { CreateEventFormData } from '../components/types';
import { serializeEventTime } from '../utils/eventSerializers';
import { EventDropArg } from '@fullcalendar/core';
import { EventResizeDoneArg } from '@fullcalendar/interaction';
import { formatDate } from '../utils/dateTimeUtils';
import { DateTime } from 'luxon';
import { logger } from '../../../../../lib/logger';

export const useEventMutation = () => {
  const [createEvent] = useCreateNylasCalendarEventMutation();
  const [updateEvent] = useUpdateNylasCalendarEventMutation();

  const handleCreateEvent = async (
    data: CreateEventFormData,
    calendarId?: string,
  ): Promise<{ success: boolean; event?: any }> => {
    try {
      const when = serializeEventTime(data);

      const response = await createEvent({
        variables: {
          input: {
            calendarId,
            data: {
              ...when,
              title: data.title,
              description: data.description,
              location: data.location,
              participants: data.participants,
              resources: data.resources,
            },
          },
        },
      });

      if (!response.data?.createNylasCalendarEvent) {
        throw new Error('Failed to create event');
      }

      customToast.success('Event created successfully');
      return {
        success: true,
        event: response.data.createNylasCalendarEvent,
      };
    } catch (error) {
      console.error('Error creating event:', error);
      customToast.error('Failed to create event');
      return { success: false };
    }
  };

  const handleUpdateEvent = async (
    calendarId: string,
    eventId: string,
    data: CreateEventFormData,
    onSuccess?: () => void,
    calendarApi?: any,
  ): Promise<boolean> => {
    try {
      const when = serializeEventTime(data);

      // Optimistically update the UI
      if (calendarApi) {
        const existingEvent = calendarApi.getEventById(eventId);
        if (existingEvent) {
          // Update the event properties
          existingEvent.setProp('title', data.title);

          // Update dates based on event type (all-day vs time-based)
          if (when.when) {
            if ('startTime' in when.when && 'endTime' in when.when) {
              // Time-based event
              existingEvent.setDates(when.when.startTime, when.when.endTime, {
                allDay: false,
              });
            } else if ('startDate' in when.when && 'endDate' in when.when) {
              // All-day event
              existingEvent.setDates(when.when.startDate, when.when.endDate, {
                allDay: true,
              });
            }
          }

          // Update other visible properties
          existingEvent.setExtendedProp('description', data.description);
          existingEvent.setExtendedProp('location', data.location);
        }
      }

      const response = await updateEvent({
        variables: {
          input: {
            calendarId,
            eventId,
            data: {
              ...when,
              title: data.title,
              description: data.description,
              location: data.location,
              participants: data.participants,
              resources: data.resources,
            },
          },
        },
      });

      if (!response.data?.updateNylasCalendarEvent) {
        throw new Error('Failed to update event');
      }

      customToast.success('Event updated successfully');
      onSuccess?.();
      return true;
    } catch (error) {
      logger.error('Error updating event:', error);
      customToast.error('Failed to update event');

      // Revert optimistic update on error
      if (calendarApi) {
        calendarApi.refetchEvents();
      }

      return false;
    }
  };

  interface EventDragOrResizeArgs {
    info: EventDropArg | EventResizeDoneArg;
    calendarId?: string;
    onSuccess?: () => void;
    onError?: () => void;
  }

  const handleEventDragOrResize = async ({
    info,
    calendarId,
    onSuccess,
    onError,
  }: EventDragOrResizeArgs): Promise<boolean> => {
    const { event } = info;

    // Optimistically update the event immediately
    if (info.view.calendar) {
      const existingEvent = info.view.calendar.getEventById(event.id);
      if (existingEvent) {
        existingEvent.setDates(event.start, event.end, {
          allDay: event.allDay,
        });
      }
    }

    try {
      const originalEvent = event.extendedProps.originalEvent;
      const isResize = 'endDelta' in info || 'startDelta' in info;
      const isDrag = 'delta' in info;

      let eventData;
      if (isResize) {
        // For resize operations, use the time deltas
        const startTimezone = originalEvent.when.startTimezone || 'UTC';
        const endTimezone = originalEvent.when.endTimezone || 'UTC';

        const originalStart = DateTime.fromISO(
          originalEvent.when.startTime,
        ).setZone(startTimezone);
        const originalEnd = DateTime.fromISO(
          originalEvent.when.endTime,
        ).setZone(endTimezone);

        let newStart = originalStart;
        let newEnd = originalEnd;

        if ('startDelta' in info) {
          newStart = originalStart.plus({
            milliseconds: info.startDelta.milliseconds,
          });
        }
        if ('endDelta' in info) {
          newEnd = originalEnd.plus({
            milliseconds: info.endDelta.milliseconds,
          });
        }

        eventData = {
          isAllDay: event.allDay,
          when: {
            startDate: formatDate(
              newStart.toISO(),
              'yyyy-MM-dd',
              startTimezone,
            ),
            endDate: formatDate(newEnd.toISO(), 'yyyy-MM-dd', endTimezone),
            startTime: newStart.toISO(),
            endTime: newEnd.toISO(),
            startTimezone,
            endTimezone,
          },
        };
      } else if (isDrag) {
        // For drag operations, we need to handle timezone conversion carefully
        const startTimezone = originalEvent.when.startTimezone || 'UTC';
        const endTimezone = originalEvent.when.endTimezone || 'UTC';

        // Get the original times in their timezones
        const originalStart = DateTime.fromISO(
          originalEvent.when.startTime,
        ).setZone(startTimezone);
        const originalEnd = DateTime.fromISO(
          originalEvent.when.endTime,
        ).setZone(endTimezone);
        const duration = originalEnd.diff(originalStart);

        // Get the new start time in the target timezone
        const newStart = DateTime.fromJSDate(event.start).setZone(
          startTimezone,
        );
        // Calculate the new end time maintaining the original duration
        const newEnd = newStart.plus(duration).setZone(endTimezone);

        eventData = {
          isAllDay: event.allDay,
          when: {
            startDate: formatDate(
              newStart.toISO(),
              'yyyy-MM-dd',
              startTimezone,
            ),
            endDate: formatDate(newEnd.toISO(), 'yyyy-MM-dd', endTimezone),
            startTime: newStart.toISO(),
            endTime: newEnd.toISO(),
            startTimezone,
            endTimezone,
          },
        };
      }

      const serialized = serializeEventTime(eventData);
      const response = await updateEvent({
        variables: {
          input: {
            calendarId,
            eventId: event.id,
            data: serialized,
          },
        },
      });

      if (!response.data?.updateNylasCalendarEvent) {
        throw new Error('Failed to update event');
      }

      customToast.success('Event updated successfully');
      onSuccess?.();

      return true;
    } catch (error) {
      // On error, revert to original position
      if (info.revert) info.revert();

      console.error('Error updating event:', error);
      customToast.error('Failed to update event');
      onError?.();
      return false;
    }
  };

  return { handleCreateEvent, handleUpdateEvent, handleEventDragOrResize };
};
